import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DroDataType, DroForm, DroFormField, DroPreviousValues, DroReadingValidation } from '../../model/dro.model';
import {
  formatDroDate, formatDroDifference, formatDroNumber, getDroConstraintSummary, toDroStoredValue, validateDroReading
} from '../../utils/dro-validation';

export type DroCapturedValues = { fieldCode: string; value: number | string | null }[];

/**
 * The technician's capture card, generated from a DRO form. Validation is live; the card reports it through
 * validationChange so the host's banner can name the first failing field. Also used read-only as the setup Preview.
 */
@Component({
  selector: 'lib-dro-capture-card',
  templateUrl: './dro-capture-card.component.html',
  styleUrl: './dro-capture-card.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ReactiveFormsModule]
})
export class DroCaptureCardComponent implements OnChanges, OnDestroy {
  @Input() form: DroForm | null = null;
  @Input() previous: DroPreviousValues = {};
  @Input() serialNumber = '';
  @Input() recorderName = '';
  @Input() preview = false;
  @Input() saving = false;

  @Output() validationChange = new EventEmitter<DroReadingValidation>();
  @Output() submitReading = new EventEmitter<DroCapturedValues>();

  formGroup = new FormGroup<Record<string, FormControl<string>>>({});
  fields: DroFormField[] = [];
  validation: DroReadingValidation = { valid: true, errors: {}, failingCount: 0, firstFailing: null };
  hasCumulative = false;
  readonly today = formatDroDate(new Date().toISOString());
  readonly dataTypes = DroDataType;
  readonly constraintSummary = getDroConstraintSummary;

  private valueChanges: Subscription | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['form']) {
      this.buildForm();
    } else if (changes['previous']) {
      this.validate(true);
    }
    if (changes['preview'] || changes['form']) {
      this.preview ? this.formGroup.disable({ emitEvent: false }) : this.formGroup.enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.valueChanges?.unsubscribe();
  }

  clear(): void {
    this.formGroup.reset();
  }

  save(): void {
    this.formGroup.markAllAsTouched();
    this.validate();
    if (!this.validation.valid || this.preview || this.saving) {
      return;
    }
    const raw = this.formGroup.getRawValue();
    this.submitReading.emit(this.fields.map(f => ({ fieldCode: f.fieldCode, value: toDroStoredValue(f, raw[f.fieldCode]) })));
  }

  showError(field: DroFormField): boolean {
    const control = this.formGroup.controls[field.fieldCode];
    return !!this.validation.errors[field.fieldCode] && !!control && (control.dirty || control.touched);
  }

  /** "+133.6" in green once the field holds a passing number and an earlier value exists. */
  gain(field: DroFormField): string {
    const previous = this.previous[field.fieldCode];
    const text = this.formGroup.controls[field.fieldCode]?.value?.trim();
    if (!previous || !text || field.dataType !== DroDataType.number || this.validation.errors[field.fieldCode]) {
      return '';
    }
    return formatDroDifference(Number(text) - previous.value, field.decimalPlaces);
  }

  previousText(field: DroFormField): string {
    const previous = this.previous[field.fieldCode];
    return previous ? formatDroNumber(previous.value, field.decimalPlaces) : '—';
  }

  private buildForm(): void {
    this.valueChanges?.unsubscribe();
    this.fields = [...(this.form?.fields ?? [])].sort((a, b) => a.position - b.position);
    this.hasCumulative = this.fields.some(f => f.cumulative);
    this.formGroup = new FormGroup<Record<string, FormControl<string>>>(
      Object.fromEntries(this.fields.map(f => [f.fieldCode, new FormControl<string>('', { nonNullable: true })]))
    );
    this.valueChanges = this.formGroup.valueChanges.subscribe(() => this.validate());
    this.validate(true);
  }

  /** Emits after the current change-detection pass when called from ngOnChanges, so host bindings never change mid-check. */
  private validate(deferEmit = false): void {
    const validation = validateDroReading(this.fields, this.formGroup.getRawValue(), this.previous);
    this.validation = validation;
    deferEmit ? queueMicrotask(() => this.validationChange.emit(validation)) : this.validationChange.emit(validation);
  }
}
